// ── V120: 중국 모드 (jjggfood.com) ────────────────────────────────────────────
//
// 허페이 매장은 VPN 없이 https://jjggfood.com 으로 연다.
// 이 주소 앞에는 Cloudflare Worker(cloudflare/jjggfood-worker.js)가 있어서
//   /__fb/<구글 API 호스트>/...  → 그 구글 API로 전달 (우리 Firebase 프로젝트 요청만 통과)
//   그 밖의 경로                → semfit.vercel.app (앱 화면·/api 그대로)
// 앱은 이 주소에서 열렸을 때만, Firebase가 부르는 구글 API 주소를 /__fb/ 로 바꿔 보낸다.
// semfit.vercel.app(한국·꼬끼니)에서는 아무것도 바뀌지 않는다.
//
// 보안 확인(App Check): reCAPTCHA는 중국에서 막혀 있어서, 중국 모드에서는 기기마다 만든
// "기기 등록 코드"(App Check 디버그 토큰)를 쓴다. 사장님이 Firebase 콘솔
// (App Check → 앱 → 디버그 토큰 관리)에 등록한 기기만 로그인·데이터 접근이 된다.
//
// ⚠ index.js 에서 가장 먼저 import 해야 한다 — Firebase가 첫 요청을 보내기 전에 바꿔 끼워야 하므로.

export const CHINA_HOSTS = ['jjggfood.com', 'www.jjggfood.com'];

const hasWindow = typeof window !== 'undefined';

export const IS_CHINA_MODE = hasWindow && (
  CHINA_HOSTS.includes(window.location.hostname) || window.__SEMFIT_FORCE_CHINA_MODE__ === true   // 후자는 테스트용
);

// Firebase SDK가 부르는 구글 API 호스트 — Worker의 허용 목록과 같아야 한다
const GOOGLE_API_HOSTS = new Set([
  'identitytoolkit.googleapis.com',          // 로그인
  'securetoken.googleapis.com',              // 로그인 유지(토큰 갱신)
  'firestore.googleapis.com',                // 데이터
  'firebasestorage.googleapis.com',          // 영수증 사진
  'content-firebaseappcheck.googleapis.com', // 보안 확인 (기기 등록 토큰)
  'firebaseinstallations.googleapis.com',
]);

const gatewayOrigin = () => (hasWindow ? window.location.origin : '');

/** 구글 API 주소면 중계 주소(/__fb/…)로 바꾼다. 중국 모드가 아니거나 해당 없으면 그대로 돌려준다. */
export function toGateway(url) {
  if (!IS_CHINA_MODE || url == null) return url;
  const s = String(url);
  const m = /^https:\/\/([a-z0-9.-]+)(\/.*)?$/i.exec(s);
  if (!m) return url;
  const host = m[1].toLowerCase();
  if (!GOOGLE_API_HOSTS.has(host)) return url;
  return `${gatewayOrigin()}/__fb/${host}${m[2] || '/'}`;
}

// ── 기기 등록 코드 (App Check 디버그 토큰) ────────────────────────────────────
const TOKEN_KEY = 'semfit_cn_device_token';

function newUuid() {
  if (hasWindow && window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  const b = new Uint8Array(16);
  if (hasWindow && window.crypto && typeof window.crypto.getRandomValues === 'function') window.crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

let memoryToken = null;
/** 이 기기의 등록 코드 — 처음 한 번 만들어 기기에 보관한다 */
export function getDeviceToken() {
  try {
    let t = window.localStorage.getItem(TOKEN_KEY);
    if (!t) { t = newUuid(); window.localStorage.setItem(TOKEN_KEY, t); }
    return t;
  } catch (e) {
    // 저장소를 못 쓰는 브라우저(사생활 보호 모드 등) — 이번 접속 동안만 유지
    if (!memoryToken) memoryToken = newUuid();
    return memoryToken;
  }
}

// Request 객체로 온 요청은 주소만 바꿔 새로 보낸다.
// new Request(새주소, 원래요청)로 감싸면 본문이 '스트리밍 업로드'가 돼서
// HTTP/1.1 연결이나 사파리에서 실패한다(Firestore 실시간 연결이 이 경로를 쓴다). 그래서 본문을 먼저 읽어 담는다.
async function refetchRequest(origFetch, to, req, init) {
  const opt = init || {};
  const method = opt.method || req.method;
  const body = 'body' in opt ? opt.body
    : (/^(GET|HEAD)$/i.test(method) ? undefined : await req.clone().arrayBuffer());
  return origFetch(to, {
    method,
    headers: opt.headers || req.headers,
    body,
    credentials: opt.credentials || req.credentials,
    cache: opt.cache || req.cache,
    redirect: opt.redirect || req.redirect,
    referrerPolicy: opt.referrerPolicy || req.referrerPolicy,
    integrity: opt.integrity || req.integrity,
    keepalive: opt.keepalive != null ? opt.keepalive : req.keepalive,
    signal: opt.signal || req.signal,
  });
}

// ── 요청 주소 바꿔 끼우기 ─────────────────────────────────────────────────────
if (IS_CHINA_MODE) {
  // App Check: 이 값이 있으면 SDK가 reCAPTCHA 대신 디버그 토큰으로 토큰을 받는다
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = getDeviceToken();

  // fetch — 로그인(Auth)·보안 확인(App Check)이 쓴다
  if (typeof window.fetch === 'function') {
    const origFetch = window.fetch.bind(window);
    window.fetch = function semfitGatewayFetch(input, init) {
      try {
        if (typeof input === 'string' || (typeof URL !== 'undefined' && input instanceof URL)) {
          const from = String(input);
          const to = toGateway(from);
          if (to !== from) return origFetch(to, init);
        } else if (input && typeof input.url === 'string') {
          const to = toGateway(input.url);
          if (to !== input.url) return refetchRequest(origFetch, to, input, init);
        }
      } catch (e) { /* 바꾸다 실패하면 원래대로 보낸다 */ }
      return origFetch(input, init);
    };
  }

  // XMLHttpRequest — 데이터(Firestore 실시간 연결)·영수증 사진(Storage)이 쓴다
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    const origOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function semfitGatewayOpen(method, url, ...rest) {
      return origOpen.call(this, method, toGateway(url), ...rest);
    };
  }
}
